import { useContext } from 'react'
import zionDebug from '@fs/zion-debug'
import { usePreference } from '@fs/zion-preferences'
import type { FeatureAttributes, FeatureFlag, FeatureFlags, Flag, Flags } from '@fs/flags-js'
import { FeatureFlagsContext } from './FeatureFlagsProvider'
import { getFeatureFlags } from './flagsJsHelper'

const debug = zionDebug('zion:flags:hooks')

type UserPreference = string | boolean | number | null | unknown[] | Record<string, unknown> | undefined
type MultipleUserPreferences = Record<string, { value: unknown }>
type Preferences = UserPreference | MultipleUserPreferences

/**
 * Examines the FeatureFlags context to determine which feature treatments should be
 * displayed to the end user.
 *
 * Pass in the name of the feature(s) to examine, and any custom attributes that should
 * be used in the treatment determination. All features must use the same traffic type.
 *
 * The treatments are returned, in the form of FeatureFlags. The FeatureFlags can be
 * deconstructed to find the specific treatments for each feature. The FeatureFlags
 * keys will exactly match the feature names supplied as input.
 */
export function useFeatureFlags<F extends Flags>(
  features: (keyof F & string)[] = [],
  attributes: FeatureAttributes = {}
): FeatureFlags<F> {
  const context = useContext(FeatureFlagsContext)
  debug('FeatureFlagProvider context', context)
  const manager = context?.splitFactory?.manager()

  if (!context || !manager) {
    // We can get here for 2 reasons:
    //  1. There is no FeatureFlagsProvider wrapper.
    //  2. There are 2 separate instances of FeatureFlagsProvider, one of which hasn't executed.
    //     See https://www.familysearch.org/frontier/docs/architectural-decisions/016-storybook-config-and-providers
    if (process.env.NODE_ENV === 'development') {
      let message = ''
      if (!manager) message += 'No SplitIO Manager. '
      if (!context) message += 'No FeatureFlagsProvider. '
      console.error(`${message}Returning "control" treatment for ${features}`)
    }
    return controlTreatments<F>(features)
  }

  const flags = getFeatureFlags(features, attributes)
  debug('useFeatureFlags with:', features, 'returning: ', flags)
  return flags as FeatureFlags<F>
}

export interface FFValues<F extends Flags> {
  data?: FeatureFlags<F>
  loading: boolean
  error: Error | null
}

export interface FFValue<F extends Flag<object>> {
  data?: FeatureFlag<F>
  loading: boolean
  error: Error | null
}

function convertPrefKeyToSplitAttributeKey(prefKey: string): string {
  return prefKey.replace('.', '_')
}

export function convertPrefsToAttributes(userPreferences: string[], prefs: Preferences): SplitIO.Attributes {
  // prefs is null if the preference asked for is not set for the user
  if (!userPreferences || userPreferences.length === 0 || prefs === null) {
    return {}
  }
  if (userPreferences.length === 1) {
    return { [convertPrefKeyToSplitAttributeKey(userPreferences[0])]: prefs as SplitIO.AttributeType }
  }

  return Object.entries(prefs as MultipleUserPreferences).reduce((aggObj, [key, value]) => {
    aggObj[convertPrefKeyToSplitAttributeKey(key)] = value.value
    return aggObj
  }, {})
}

/**
 * Examines the FeatureFlags context to determine which feature treatments should be
 * displayed to the end user.
 *
 * Pass in the name of the feature(s) to examine, and any custom attributes that should
 * be used in the treatment determination. All features must use the same traffic type.
 *
 * The treatments are returned, in the form of FeatureFlags. The FeatureFlags can be
 * deconstructed to find the specific treatments for each feature. The FeatureFlags
 * keys will exactly match the feature names supplied as input.
 */
export function useFeatureFlagsWithPrefs<F extends Flags>(
  features: (keyof F & string)[] = [],
  attributes: FeatureAttributes = {},
  options: { userPreferences: string[] } | undefined
): FFValues<F> {
  const context = useContext(FeatureFlagsContext)
  debug('FeatureFlagProvider context', context)
  const manager = context?.splitFactory?.manager()

  const prefs: Preferences = usePreference(...options.userPreferences)

  // undefined prefs means the promise hasn't resolved to go get the prefs yet
  if (prefs === undefined) {
    return { loading: true, error: null }
  }

  const prefAttributes = convertPrefsToAttributes(options.userPreferences, prefs)

  if (!context || !manager) {
    // We can get here for 2 reasons:
    //  1. There is no FeatureFlagsProvider wrapper.
    //  2. There are 2 separate instances of FeatureFlagsProvider, one of which hasn't executed.
    //     See https://www.familysearch.org/frontier/docs/architectural-decisions/016-storybook-config-and-providers
    if (process.env.NODE_ENV === 'development') {
      let message = ''
      if (!manager) message += 'No SplitIO Manager. '
      if (!context) message += 'No FeatureFlagsProvider. '
      console.error(`${message}Returning "control" treatment for ${features}`)
    }
    return { data: controlTreatments<F>(features), loading: false, error: null }
  }

  const combinedAttrs = { ...attributes, ...prefAttributes }
  const flags = getFeatureFlags(features, combinedAttrs)
  debug('feature flags', flags)
  return { data: flags as FeatureFlags<F>, loading: false, error: null }
}

function controlTreatments<F extends Flags>(features: (keyof F & string)[]): FeatureFlags<F> {
  return features.reduce((acc, f) => {
    acc[f] = { treatment: 'control', config: null, isOn: false, isControl: true } as FeatureFlag<F[typeof f]>
    return acc
  }, {} as FeatureFlags<F>)
}

/**
 * Convenience function that takes one feature and returns the treatment.
 */
export function useFeatureFlag<F extends Flag<object>>(
  feature: string,
  attributes: FeatureAttributes = {}
): FeatureFlag<F> {
  const flags = useFeatureFlags([feature], attributes)
  return flags[feature] as FeatureFlag<F>
}

/**
 * Convenience function that takes one feature and returns the treatment.
 */
export function useFeatureFlagWithPrefs<F extends Flag<object>>(
  feature: string,
  attributes: FeatureAttributes = {},
  options: { userPreferences: string[] }
): FFValue<F> {
  const { data, loading, error } = useFeatureFlagsWithPrefs([feature], attributes, options)
  return { data: data?.[feature] as FeatureFlag<F>, loading, error }
}
